<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import MxButton from '@/components/MxButton.vue';
import MxIcon from '@/components/MxIcon.vue';

const userStore = useUserStore();
const { me } = storeToRefs(userStore);
const initialBalance = ref<number | null>(null);
const credited = ref(false);
const timedOut = ref(false);
let timer: ReturnType<typeof setInterval> | null = null;
let stopAt = 0;

onMounted(async () => {
  await userStore.load();
  initialBalance.value = me.value?.balance_tokens ?? 0;
  // Real card payments via Prodamus often take 1–3 minutes; give them
  // headroom before falling back to "still in processing".
  stopAt = Date.now() + 5 * 60_000;
  timer = setInterval(async () => {
    await userStore.load();
    if ((me.value?.balance_tokens ?? 0) > (initialBalance.value ?? 0)) {
      credited.value = true;
      if (timer) clearInterval(timer);
    } else if (Date.now() > stopAt) {
      timedOut.value = true;
      if (timer) clearInterval(timer);
    }
  }, 5000);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <section class="ts" :class="{ 'ts--pending': !credited && !timedOut }">
    <MxIcon
      :name="credited ? 'CheckCircle2' : timedOut ? 'Hourglass' : 'Loader'"
      :size="64"
    />
    <h1 v-if="credited">Платёж принят</h1>
    <h1 v-else-if="timedOut">Платёж в обработке</h1>
    <h1 v-else>Ждём оплату</h1>

    <p v-if="credited" class="ok">+{{ (me?.balance_tokens ?? 0) - (initialBalance ?? 0) }} т. на балансе</p>
    <p v-else-if="timedOut" class="warn">Когда оплата дойдёт до системы — баланс обновится сам, и придёт уведомление в чат.</p>
    <p v-else class="muted">Заверши оплату в открытой вкладке. Эта страница сама обновится, когда токены придут.</p>

    <RouterLink to="/">
      <MxButton variant="primary">На главную</MxButton>
    </RouterLink>
  </section>
</template>

<style scoped>
.ts { padding: var(--sp-12) var(--sp-4); display: flex; flex-direction: column; align-items: center; gap: var(--sp-3); text-align: center; color: var(--success); }
.ts h1 { font-family: var(--font-display); font-size: var(--fs-2xl); color: var(--text-primary); }
.ok { color: var(--accent); font-weight: 600; }
.warn { color: var(--warning); }
.muted { color: var(--text-secondary); }
</style>
